import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Search, Download } from "lucide-react";
import api, { apiError, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const roleLabel = { admin: "Admin", practitioner: "Practitioner", seeker: "Seeker" };

export const UsersManager = ({ onChange }) => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.id}`);
      toast.success(`${target.email} removed`);
      setTarget(null);
      load();
      onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const q = query.trim().toLowerCase();
  const shown = users.filter((u) => !q || u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.business_name || "").toLowerCase().includes(q));

  return (
    <div data-testid="users-manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-lg md:text-2xl text-foreground">Users</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="users-count">{users.length} account{users.length !== 1 ? "s" : ""} · seekers and practitioners</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="users-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by email, name or business" className="pl-9 h-9 text-sm" />
          </div>
          <Button size="sm" variant="outline" className="rounded-full h-9" data-testid="users-export-btn"
            onClick={() => downloadCsv("/admin/users/export.csv", "orec-users.csv").then(() => toast.success("Users list downloaded")).catch((e) => toast.error(apiError(e)))}>
            <Download size={13} className="mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="users-list">
        {shown.map((u) => (
          <div key={u.id} className="silk-card rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-foreground truncate" data-testid={`user-email-${u.id}`}>{u.email}</p>
              <p className="text-xs text-muted-foreground truncate">
                {u.name}{u.business_name ? ` · ${u.business_name}${u.practitioner_status ? ` (${u.practitioner_status})` : ""}` : ""} · joined {new Date(u.created_at).toLocaleDateString("en-CA")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={u.role === "admin" ? "default" : "secondary"} className="font-normal" data-testid={`user-role-${u.id}`}>{roleLabel[u.role] || u.role}</Badge>
              {u.role === "admin" ? (
                <span className="text-[11px] text-muted-foreground" data-testid={`user-protected-${u.id}`}>Protected</span>
              ) : (
                <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground" data-testid={`user-delete-btn-${u.id}`} onClick={() => setTarget(u)}>
                  <Trash2 size={13} className="mr-1.5" /> Delete
                </Button>
              )}
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground" data-testid="users-empty">No accounts match.</p>}
      </div>

      <AlertDialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {target?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the account{target?.business_name ? `, the practitioner profile "${target.business_name}"` : ""}, all of their active sign-in sessions, and their entry on the email list. Booking and payment records are kept for your books. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel" disabled={busy}>Keep account</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); confirmDelete(); }}>
              {busy ? "Deleting…" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
